import { useEffect, useState } from 'react';
import { cx } from './cx';

export interface ActionSheetAction {
  label: string;
  icon?: React.ReactNode;
  onSelect: () => void;
  disabled?: boolean;
}

export interface ActionSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  actions: ActionSheetAction[];
}

const ActionSheet: React.FC<ActionSheetProps> = ({ open, onClose, title, actions }) => {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="action-sheet-backdrop"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title ?? 'Actions'}
        className={cx(
          'w-full max-w-md rounded-t-2xl bg-white dark:bg-bg-dark-secondary border-t border-meradio-light dark:border-border-dark shadow-2xl pb-[env(safe-area-inset-bottom)] transition-transform duration-300 ease-out',
          entered ? 'translate-y-0' : 'translate-y-full'
        )}
        onClick={(event) => event.stopPropagation()}
      >
        {title && (
          <div className="px-4 py-3 border-b border-meradio-light dark:border-border-dark">
            <p className="text-sm font-semibold text-meradio-dark dark:text-text-dark-primary truncate">
              {title}
            </p>
          </div>
        )}
        <div className="p-2">
          {actions.map((action, index) => (
            <button
              key={index}
              type="button"
              disabled={action.disabled}
              onClick={() => {
                if (action.disabled) return;
                action.onSelect();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm font-medium text-meradio-dark dark:text-text-dark-primary transition-colors hover:bg-meradio-light/40 dark:hover:bg-bg-dark-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-meradio-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {action.icon && (
                <span className="flex-shrink-0 text-meradio-dark-light dark:text-text-dark-secondary">
                  {action.icon}
                </span>
              )}
              <span className="truncate">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ActionSheet;
